import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Newspaper,
    Search,
    Calendar,
    User,
    Tag,
    ArrowUpRight,
    ChevronLeft,
    ChevronRight,
    Sparkles,
    TrendingUp,
    Filter,
} from "lucide-react";
import AnimatedSection, {
    StaggerContainer,
    StaggerItem,
} from "../../components/ui/AnimatedSection";

interface NewsArticle {
    id: number;
    title: string;
    excerpt: string;
    content: string;
    category: string;
    author: string;
    date: string;
    image: string;
    featured?: boolean;
}

const newsArticles: NewsArticle[] = [
    {
        id: 1,
        title: "Understanding Mutual Funds: A Comprehensive Guide for Beginners",
        excerpt:
            "Learn the basics of mutual fund investing and how to choose the right funds for your portfolio.",
        content: "Full article content here...",
        category: "Investment Education",
        author: "Rajesh Kumar",
        date: "2024-01-15",
        image: "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?q=80&w=2070",
        featured: true,
    },
    {
        id: 2,
        title: "2024 Budget Impact on Tax Planning and Investments",
        excerpt:
            "Analysis of how the latest budget announcements affect your tax planning and investment strategies.",
        content: "Full article content here...",
        category: "Market Analysis",
        author: "Priya Sharma",
        date: "2024-01-12",
        image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=2070",
        featured: true,
    },
    {
        id: 3,
        title: "Gold vs. Equity: Where Should You Invest in 2024?",
        excerpt:
            "Compare the performance and potential of gold and equity investments in the current market scenario.",
        content: "Full article content here...",
        category: "Investment Tips",
        author: "Amit Verma",
        date: "2024-01-10",
        image: "https://images.unsplash.com/photo-1559526324-593bc073d938?q=80&w=2070",
    },
    {
        id: 4,
        title: "How to Build a Retirement Portfolio in Your 30s",
        excerpt:
            "Start planning for retirement early with our expert-backed strategies for building wealth.",
        content: "Full article content here...",
        category: "Retirement Planning",
        author: "Sneha Patel",
        date: "2024-01-08",
        image: "https://images.unsplash.com/photo-1434626881859-194d67b2b86f?q=80&w=2074",
    },
    {
        id: 5,
        title: "RBI Policy Changes: What It Means for Your Savings",
        excerpt:
            "Understand the recent RBI monetary policy changes and their impact on savings and investments.",
        content: "Full article content here...",
        category: "Market Analysis",
        author: "Vikram Singh",
        date: "2024-01-05",
        image: "https://images.unsplash.com/photo-1590283603385-17ffb3a7f29f?q=80&w=2070",
    },
    {
        id: 6,
        title: "Tax-Saving Investments Under Section 80C Explained",
        excerpt:
            "A complete guide to tax-saving investment options available under Section 80C.",
        content: "Full article content here...",
        category: "Tax Planning",
        author: "Anjali Reddy",
        date: "2024-01-03",
        image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2026",
    },
    {
        id: 7,
        title: "SIP vs Lump Sum: Which Investment Strategy Works Best?",
        excerpt:
            "Compare systematic investment plans with lump sum investments to find the right approach.",
        content: "Full article content here...",
        category: "Investment Education",
        author: "Karthik Menon",
        date: "2024-01-01",
        image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?q=80&w=2070",
    },
    {
        id: 8,
        title: "Top 5 Investment Mistakes to Avoid in 2024",
        excerpt:
            "Learn from common investment mistakes and how to avoid them for better returns.",
        content: "Full article content here...",
        category: "Investment Tips",
        author: "Deepak Joshi",
        date: "2023-12-28",
        image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=2071",
    },
    {
        id: 9,
        title: "Insurance Planning: How Much Coverage Do You Really Need?",
        excerpt:
            "Determine the right amount of life and health insurance coverage for your family's needs.",
        content: "Full article content here...",
        category: "Insurance",
        author: "Meera Krishnan",
        date: "2023-12-25",
        image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070",
    },
    {
        id: 10,
        title: "Understanding ELSS Funds: Tax Benefits and Returns",
        excerpt:
            "Explore Equity Linked Savings Schemes and how they combine tax benefits with market returns.",
        content: "Full article content here...",
        category: "Tax Planning",
        author: "Suresh Iyer",
        date: "2023-12-22",
        image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=2015",
    },
    {
        id: 11,
        title: "Real Estate vs Stock Market: Where to Invest in 2024?",
        excerpt:
            "Compare the pros and cons of real estate and stock market investments for long-term wealth.",
        content: "Full article content here...",
        category: "Market Analysis",
        author: "Rahul Kapoor",
        date: "2023-12-20",
        image: "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?q=80&w=2073",
    },
    {
        id: 12,
        title: "Child Education Planning: Start Early, Secure Their Future",
        excerpt:
            "Learn how to plan and invest for your child's education expenses with smart strategies.",
        content: "Full article content here...",
        category: "Financial Planning",
        author: "Pooja Nair",
        date: "2023-12-18",
        image: "https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?q=80&w=2070",
    },
];

const categories = [
    "All",
    "Investment Education",
    "Market Analysis",
    "Investment Tips",
    "Retirement Planning",
    "Tax Planning",
    "Insurance",
    "Financial Planning",
];

const sortOptions = [
    { value: "latest", label: "Latest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "title", label: "Title (A-Z)" },
];

const ITEMS_PER_PAGE = 6;

export default function NewsPage() {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");
    const [sortBy, setSortBy] = useState("latest");
    const [currentPage, setCurrentPage] = useState(1);

    // Filter and sort articles
    const filteredAndSortedArticles = useMemo(() => {
        let filtered = newsArticles.filter((article) => {
            const matchesSearch =
                article.title
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                article.excerpt
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase());
            const matchesCategory =
                selectedCategory === "All" ||
                article.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });

        // Sort articles
        filtered.sort((a, b) => {
            if (sortBy === "latest") {
                return new Date(b.date).getTime() - new Date(a.date).getTime();
            } else if (sortBy === "oldest") {
                return new Date(a.date).getTime() - new Date(b.date).getTime();
            } else if (sortBy === "title") {
                return a.title.localeCompare(b.title);
            }
            return 0;
        });

        return filtered;
    }, [searchQuery, selectedCategory, sortBy]);

    // Pagination
    const totalPages = Math.ceil(
        filteredAndSortedArticles.length / ITEMS_PER_PAGE
    );
    const paginatedArticles = filteredAndSortedArticles.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset to page 1 when filters change
    const handleFilterChange = (
        filterType: "search" | "category" | "sort",
        value: string
    ) => {
        setCurrentPage(1);
        if (filterType === "search") setSearchQuery(value);
        else if (filterType === "category") setSelectedCategory(value);
        else if (filterType === "sort") setSortBy(value);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    return (
        <div className="overflow-hidden">
            {/* Hero */}
            <section className="relative py-32 bg-cream">
                <div className="absolute inset-0 bg-pattern" />
                <div className="absolute top-0 left-0 w-1/2 h-full bg-gradient-to-r from-[#d4b86a]/5 to-transparent" />

                <div className="container-custom px-6 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center max-w-4xl mx-auto"
                    >
                        <span className="badge mb-4">
                            <Sparkles className="w-3 h-3 mr-2" />
                            Latest News & Insights
                        </span>
                        <h1 className="text-gray-900 mb-6">
                            Stay Informed with
                            <span className="block mt-1 gradient-text">
                                Expert Financial Insights
                            </span>
                        </h1>
                        <p className="text-xl text-gray-600 leading-relaxed">
                            Get the latest updates on investments, market
                            trends, tax planning, and financial strategies from
                            our expert team.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Search and Filters */}
            <section className="section-padding bg-white border-b border-gray-100">
                <div className="container-custom">
                    <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between mb-8">
                        {/* Search Bar */}
                        <div className="relative flex-1 max-w-md w-full">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search articles..."
                                value={searchQuery}
                                onChange={(e) =>
                                    handleFilterChange("search", e.target.value)
                                }
                                className="w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/20 focus:border-[#c9a84c] transition-all"
                            />
                        </div>

                        {/* Sort Dropdown */}
                        <div className="flex items-center space-x-3">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={(e) =>
                                    handleFilterChange("sort", e.target.value)
                                }
                                className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/20 focus:border-[#c9a84c] transition-all cursor-pointer"
                            >
                                {sortOptions.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Category Filters */}
                    <div className="flex flex-wrap gap-3">
                        {categories.map((category) => (
                            <motion.button
                                key={category}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() =>
                                    handleFilterChange("category", category)
                                }
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                    selectedCategory === category
                                        ? "bg-[#c9a84c] text-white shadow-md"
                                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                                }`}
                            >
                                {category}
                            </motion.button>
                        ))}
                    </div>

                    {/* Results Count */}
                    <div className="mt-6 text-sm text-gray-500">
                        Showing {paginatedArticles.length} of{" "}
                        {filteredAndSortedArticles.length} articles
                    </div>
                </div>
            </section>

            {/* News Grid */}
            <section className="section-padding bg-offwhite">
                <div className="container-custom">
                    <AnimatePresence mode="wait">
                        {paginatedArticles.length > 0 ? (
                            <motion.div
                                key={`${selectedCategory}-${sortBy}-${currentPage}`}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {paginatedArticles.map((article) => (
                                        <StaggerItem key={article.id}>
                                            <motion.article
                                                whileHover={{ y: -6 }}
                                                className="glass-card-hover rounded-xl overflow-hidden h-full flex flex-col"
                                            >
                                                {/* Image */}
                                                <div className="relative h-48 overflow-hidden">
                                                    <img
                                                        src={article.image}
                                                        alt={article.title}
                                                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                                    />
                                                    {article.featured && (
                                                        <div className="absolute top-3 right-3 bg-[#c9a84c] text-white px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1">
                                                            <TrendingUp className="w-3 h-3" />
                                                            <span>Featured</span>
                                                        </div>
                                                    )}
                                                    <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                                                        <span className="text-xs font-medium text-gray-700 flex items-center">
                                                            <Tag className="w-3 h-3 mr-1 text-[#c9a84c]" />
                                                            {article.category}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Content */}
                                                <div className="p-6 flex-1 flex flex-col">
                                                    <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                                                        <span className="flex items-center">
                                                            <Calendar className="w-3 h-3 mr-1" />
                                                            {formatDate(
                                                                article.date
                                                            )}
                                                        </span>
                                                        <span className="flex items-center">
                                                            <User className="w-3 h-3 mr-1" />
                                                            {article.author}
                                                        </span>
                                                    </div>

                                                    <h3 className="text-lg font-display text-gray-900 mb-2 line-clamp-2">
                                                        {article.title}
                                                    </h3>

                                                    <p className="text-sm text-gray-600 mb-4 flex-grow line-clamp-3">
                                                        {article.excerpt}
                                                    </p>

                                                    <Link
                                                        to={`/news/${article.id}`}
                                                        className="inline-flex items-center space-x-2 text-[#c9a84c] hover:text-[#b8973e] font-medium text-sm transition-colors mt-auto"
                                                    >
                                                        <span>Read More</span>
                                                        <ArrowUpRight className="w-4 h-4" />
                                                    </Link>
                                                </div>
                                            </motion.article>
                                        </StaggerItem>
                                    ))}
                                </StaggerContainer>
                            </motion.div>
                        ) : (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-center py-16"
                            >
                                <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="text-xl font-display text-gray-900 mb-2">
                                    No articles found
                                </h3>
                                <p className="text-gray-600">
                                    Try adjusting your search or filter criteria
                                </p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mt-12 flex items-center justify-center space-x-2"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                    setCurrentPage((p) => Math.max(1, p - 1))
                                }
                                disabled={currentPage === 1}
                                className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronLeft className="w-5 h-5 text-gray-600" />
                            </motion.button>

                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(
                                    (page) =>
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 1 &&
                                            page <= currentPage + 1)
                                )
                                .map((page, index, array) => (
                                    <>
                                        {index > 0 &&
                                            array[index - 1] !== page - 1 && (
                                                <span
                                                    key={`ellipsis-${page}`}
                                                    className="px-2 text-gray-400"
                                                >
                                                    ...
                                                </span>
                                            )}
                                        <motion.button
                                            key={page}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={() => setCurrentPage(page)}
                                            className={`w-10 h-10 rounded-lg font-medium transition-all ${
                                                currentPage === page
                                                    ? "bg-[#c9a84c] text-white shadow-md"
                                                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                                            }`}
                                        >
                                            {page}
                                        </motion.button>
                                    </>
                                ))}

                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() =>
                                    setCurrentPage((p) =>
                                        Math.min(totalPages, p + 1)
                                    )
                                }
                                disabled={currentPage === totalPages}
                                className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                            >
                                <ChevronRight className="w-5 h-5 text-gray-600" />
                            </motion.button>
                        </motion.div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="py-16 bg-gradient-to-br from-gray-900 to-gray-800 section-dark">
                <div className="container-custom px-6 text-center relative z-10">
                    <AnimatedSection>
                        <h2 className="text-white mb-4">
                            Want Personalized{" "}
                            <span className="gradient-text">
                                Financial Advice
                            </span>
                            ?
                        </h2>
                        <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
                            Connect with our expert advisors for tailored
                            financial solutions
                        </p>
                        <Link to="/contact">
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn-primary inline-flex items-center space-x-2"
                            >
                                <span>Schedule Consultation</span>
                                <ArrowUpRight className="w-4 h-4" />
                            </motion.button>
                        </Link>
                    </AnimatedSection>
                </div>
            </section>
        </div>
    );
}
